import { useRef } from 'react';
import { cn } from '../../lib/utils';
import { fieldWrapper, fieldWrapperInput } from './field-surface';

type MarginMode = 'percentage' | 'fixed';

interface MarginInputProps {
	value: string;
	onChange: (value: string) => void;
	mode: MarginMode;
	/**
	 * Omit to lock the unit. The toggle still shows which one is in force — it
	 * just stops offering the other, for a value whose basis can't be changed
	 * after the fact (an adjustment already authored, say).
	 */
	onModeChange?: (mode: MarginMode) => void;
	currency: string;
	className?: string;
}

export function MarginInput({
	value,
	onChange,
	mode,
	onModeChange,
	currency,
	className,
}: MarginInputProps) {
	const ref = useRef<HTMLInputElement>(null);

	return (
		<div className={cn(fieldWrapper, className)}>
			<input
				ref={ref}
				type="number"
				data-slot="input"
				value={value}
				onChange={(e) => onChange(e.target.value)}
				placeholder={mode === 'percentage' ? 'e.g. 20' : '0.00'}
				min={0}
				max={mode === 'percentage' ? 100 : undefined}
				step={mode === 'percentage' ? 0.1 : 0.01}
				className={cn(
					fieldWrapperInput,
					'[&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none'
				)}
			/>
			<div className="flex shrink-0 border-l border-line-2">
				<button
					type="button"
					tabIndex={-1}
					disabled={!onModeChange}
					onClick={() => onModeChange?.('percentage')}
					className={cn(
						'flex min-w-7 items-center justify-center px-2 text-xs font-medium transition-colors',
						mode === 'percentage'
							? 'bg-surface-input text-text-1'
							: 'bg-surface-fill text-text-4',
						onModeChange
							? mode !== 'percentage' && 'hover:text-text-2'
							: 'cursor-default'
					)}
				>
					%
				</button>
				<div className="w-px bg-input" />
				<button
					type="button"
					tabIndex={-1}
					disabled={!onModeChange}
					onClick={() => onModeChange?.('fixed')}
					className={cn(
						'flex items-center justify-center px-2 text-xs font-medium transition-colors',
						mode === 'fixed'
							? 'bg-surface-input text-text-1'
							: 'bg-surface-fill text-text-4',
						onModeChange ? mode !== 'fixed' && 'hover:text-text-2' : 'cursor-default'
					)}
				>
					{currency}
				</button>
			</div>
		</div>
	);
}
